import {Component, OnInit} from '@angular/core';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {ObjectBaseModeComponent} from 'features/core/shared/object/mvs-object-base/object-base-mode.component';
import {Sorting} from 'features/core/shared/misc/sorting';
import {BdDunningTypeChildTable} from '../../../../../interface/bd-dunning-type-child-table.interface';

/**
 * Base mode component of the DunningType object component — layer 2 of the three-layer OBC pattern
 * (root / view-base / view-full + view-side / shared).
 *
 * The class is never rendered itself (`template: ''`), it only carries the two declarative tables
 * that {@link BdDunningTypeFullComponent} and {@link BdDunningTypeSideComponent} render from:
 *   - {@link navigationItems} — the left navigation of the object page,
 *   - {@link childTables}     — the descriptor of every navigation point that is a plain
 *                               parent-filtered entity table.
 *
 * `bd.DunningType` is a COMPLEX configuration entity (ten same-module configuration children), so
 * the left navigation is the mandatory shape: the config page shows a flat table, a row click opens
 * this object page, and the children are reached through the navigation — never through inline tabs
 * on either page.
 *
 * `ObjectBaseModeComponent` already subscribes to `?navItem=` / `?sideNavItem=` and resolves the
 * matching entry by its `action`, so nothing has to be rebuilt here.
 *
 * Reference implementation followed: `features/feature-prov/cp/component/object-components/
 * cp-provision-type-object/view/base/cp-provision-type-base.component.ts`.
 *
 * UI text is German with real umlauts; identifiers and comments are English.
 */
@Component({
    selector: 'bd-dunning-type-base',
    template: '',
    standalone: false
})
export class BdDunningTypeBaseComponent extends ObjectBaseModeComponent implements OnInit {

    /**
     * The twelve navigation points of the dunning type, ordered along the business flow a
     * caseworker reads the configuration in: first the two overall views (the read-only summary and
     * the graphical node view), then the entry into the dunning run, then the levels with
     * everything hanging off them, then the money, then the exits and the two reason lists.
     *
     * `action` (not `route`) is the key the framework matches `?navItem=` against, so every single
     * view is deep-linkable. `toggleable: false` everywhere: none of these points has a sub-list
     * that could be folded open.
     *
     * `overview` is the default — the landing view of the object page is the read-only summary,
     * not an editable table.
     */
    navigationItems: NavigationItem[] = [
        {
            label: 'Übersicht',
            action: 'overview',
            tooltip: 'Lesende Gesamtübersicht der Mahnart mit Beispielrechnung',
            icon: 'fa-solid fa-list-check',
            toggleable: false,
            default: true
        },
        {
            label: 'Knotenansicht',
            action: 'nodeView',
            tooltip: 'Grafische Konfigurationsansicht aller Bestandteile',
            icon: 'fa-solid fa-diagram-project',
            toggleable: false,
            default: false
        },
        {
            label: 'Startbedingungen',
            action: 'starts',
            tooltip: 'Einstieg in das Mahnverfahren je Pfad und Abrechnungsbereich',
            icon: 'fa-solid fa-circle-play',
            toggleable: false,
            default: false
        },
        {
            label: 'Stufen',
            action: 'levels',
            tooltip: 'Mahnstufen (M1…M4)',
            icon: 'fa-solid fa-layer-group',
            toggleable: false,
            default: false
        },
        {
            label: 'Stufenaktionen',
            action: 'levelActions',
            tooltip: 'Aktionen je Mahnstufe',
            icon: 'fa-solid fa-bolt',
            toggleable: false,
            default: false
        },
        {
            label: 'Stufenübergänge',
            action: 'levelTransitions',
            tooltip: 'Übergänge zwischen den Mahnstufen',
            icon: 'fa-solid fa-arrow-right-arrow-left',
            toggleable: false,
            default: false
        },
        {
            label: 'Gebühren',
            action: 'charges',
            tooltip: 'Mahngebühren dieser Mahnart',
            icon: 'fa-solid fa-money-bill-wave',
            toggleable: false,
            default: false
        },
        {
            label: 'Stoppbedingungen',
            action: 'stops',
            tooltip: 'Bedingungen, unter denen das Mahnverfahren stoppt',
            icon: 'fa-solid fa-circle-stop',
            toggleable: false,
            default: false
        },
        {
            label: 'Manuelle Transaktionen',
            action: 'manualTransactions',
            tooltip: 'Erlaubte manuelle Buchungsarten dieser Mahnart',
            icon: 'fa-solid fa-money-check-dollar',
            toggleable: false,
            default: false
        },
        {
            label: 'Einmalzahlungen',
            action: 'oneTimePayments',
            tooltip: 'Einmalzahlungen dieser Mahnart',
            icon: 'fa-solid fa-hand-holding-dollar',
            toggleable: false,
            default: false
        },
        {
            label: 'Stornogründe',
            action: 'terminationReasons',
            tooltip: 'Stornogründe je Geltungsbereich',
            icon: 'fa-solid fa-ban',
            toggleable: false,
            default: false
        },
        {
            label: 'Mahnpause-Gründe',
            action: 'pauseReasons',
            tooltip: 'Auswählbare Gründe im Mahnpause-Dialog',
            icon: 'fa-solid fa-circle-pause',
            toggleable: false,
            default: false
        }
    ];

    /**
     * Descriptors of the ten plain child lists, keyed by the navigation `action`.
     *
     * Every entry drives ONE generic entity table filtered on the parent FK `dunningType`; the
     * rendering itself happens exactly once, in {@link BdDunningTypeChildTableComponent}. The widget
     * `alias` is the persistence key of the saved table layout, so the eight aliases that already
     * exist are kept verbatim — renaming them would silently drop every column order, width and
     * sorting users have saved. Only the two reason tables get a new alias, because they never had
     * a widget before (they were hand-written editors).
     *
     * `label` doubles as the widget title, which is why it repeats the German navigation label.
     *
     * NOT in this map, because they are not plain tables:
     *   `overview` — the read-only summary component,
     *   `nodeView` — the graphical canvas (full view only).
     */
    readonly childTables: { [action: string]: BdDunningTypeChildTable } = {
        starts: {
            alias: 'bd.dunning-type.config.start.table',
            label: 'Startbedingungen',
            entity: 'bd.DunningTypeStart',
            noDataText: 'Keine Startbedingungen konfiguriert.'
        },
        levels: {
            alias: 'bd.dunning-type.config.levels.table',
            label: 'Stufen',
            entity: 'bd.DunningTypeLevel',
            noDataText: 'Keine Stufen konfiguriert.'
        },
        levelActions: {
            alias: 'bd.dunning-type.config.level-actions.table',
            label: 'Stufenaktionen',
            entity: 'bd.DunningTypeLevelAction',
            noDataText: 'Keine Stufenaktionen konfiguriert.'
        },
        levelTransitions: {
            alias: 'bd.dunning-type.config.level-transitions.table',
            label: 'Stufenübergänge',
            entity: 'bd.DunningTypeLevelTransition',
            noDataText: 'Keine Stufenübergänge konfiguriert.'
        },
        charges: {
            alias: 'bd.dunning-type.config.charges.table',
            label: 'Gebühren',
            entity: 'bd.DunningTypeCharge',
            noDataText: 'Keine Gebühren konfiguriert.'
        },
        stops: {
            alias: 'bd.dunning-type.config.stop.table',
            label: 'Stoppbedingungen',
            entity: 'bd.DunningTypeStop',
            noDataText: 'Keine Stoppbedingungen konfiguriert.'
        },
        manualTransactions: {
            alias: 'bd.dunning-type.config.manual-transactions.table',
            label: 'Manuelle Transaktionen',
            entity: 'bd.DunningTypeManualTransaction',
            noDataText: 'Keine manuellen Transaktionen konfiguriert.'
        },
        oneTimePayments: {
            alias: 'bd.dunning-type.config.one-time-payments.table',
            label: 'Einmalzahlungen',
            entity: 'bd.DunningTypeOneTimePayment',
            noDataText: 'Keine Einmalzahlungen konfiguriert.'
        },
        /**
         * Replaces the hand-written Stornogrund editor. Sorted ascending by id so the list keeps a
         * stable, insertion-like order — the entity has no order attribute of its own.
         *
         * `defaultActiveStatus` pre-fills the status ACTIVE on create: the backend only ever
         * evaluates ACTIVE reasons, and the old editor created rows in draft status, which the
         * dunning run then silently ignored.
         */
        terminationReasons: {
            alias: 'bd.dunning-type.config.termination-reasons.table',
            label: 'Stornogründe',
            entity: 'bd.DunningTypeTerminationReason',
            noDataText: 'Keine Stornogründe zugeordnet.',
            sorting: [new Sorting('id', true)],
            defaultActiveStatus: true
        },
        /** Replaces the hand-written Mahnpause-Grund editor; same reasoning as above. */
        pauseReasons: {
            alias: 'bd.dunning-type.config.pause-reasons.table',
            label: 'Mahnpause-Gründe',
            entity: 'bd.DunningTypePauseReason',
            noDataText: 'Keine Mahnpause-Gründe konfiguriert.',
            sorting: [new Sorting('id', true)],
            defaultActiveStatus: true
        }
    };

    ngOnInit() {
        super.ngOnInit();
        this.initialized = true;
    }

}
